import { getCategoryProfile } from "../category-profiles";
import type { IntakeCategoryProfile } from "../category-profiles";
import { DEFAULT_FORM_CONFIG, MEDICAL_CONTRACT_OPTIONS, STANDARD_CONTRACT_OPTIONS, type FormConfigVersion } from "../form-config";
import type { FileUploadDefinition, IntakeData, IntakeField, IntakeStep, RepeaterField } from "../types";
import { isUpsellContractType, resolveConsultationProductName } from "./default-data";
import { getBookingQuestionChecklistHelperText } from "./intake-steps";

function patchField(fields: IntakeField[] | undefined, fieldId: string, patch: Partial<IntakeField>): IntakeField[] | undefined {
  return fields?.map((field) => (field.id === fieldId ? { ...field, ...patch } : field));
}

function patchRepeaterFields(step: IntakeStep, repeaterId: string, patcher: (fields: RepeaterFieldField[]) => RepeaterFieldField[]): IntakeStep {
  return {
    ...step,
    repeaters: step.repeaters?.map((repeater) => (
      repeater.id === repeaterId ? { ...repeater, fields: patcher(repeater.fields as RepeaterFieldField[]) } : repeater
    )),
  };
}

type RepeaterFieldField = RepeaterField["fields"][number];

const MEDICAL_ONLY_POLICY_FIELD_IDS = new Set(["consultationFeePolicy", "consultationFeeAmount"]);

function patchRepeaterField(fields: RepeaterFieldField[], fieldId: string, patch: Partial<RepeaterFieldField>): RepeaterFieldField[] {
  return fields.map((field) => (field.id === fieldId ? { ...field, ...patch } : field));
}

function getLastHangulChar(value: string): string | null {
  return Array.from(value.trim()).reverse().find((char) => /[가-힣]/.test(char)) ?? null;
}

function hasFinalConsonant(value: string): boolean {
  const char = getLastHangulChar(value);
  return char ? (char.charCodeAt(0) - 0xac00) % 28 > 0 : false;
}

function withObjectParticle(value: string): string {
  return `${value}${hasFinalConsonant(value) ? "을" : "를"}`;
}

function withSubjectParticle(value: string): string {
  return `${value}${hasFinalConsonant(value) ? "이" : "가"}`;
}

function shouldRequireInteriorExteriorPhotos(profile: IntakeCategoryProfile): boolean {
  return ["medical", "beauty", "experience"].includes(profile.domain) || profile.id === "fnb_restaurant";
}

function getInteriorExteriorPhotoCopy(profile: IntakeCategoryProfile): Pick<FileUploadDefinition, "label" | "description" | "required"> {
  if (shouldRequireInteriorExteriorPhotos(profile)) {
    return {
      label: `${profile.facilityNoun} 내부 / 외부 사진`,
      description: `${profile.facilityNoun} 입구, 내부, 대기공간, 이용 공간처럼 방문 안내에 도움이 되는 사진을 첨부해주세요.`,
      required: true,
    };
  }

  return {
    label: "공간 / 현장 안내 사진 (해당 시)",
    description: "매장, 수령지, 미팅 장소, 차량, 이용 현장처럼 고객 안내에 도움이 되는 사진이 있다면 첨부해주세요. 실제 공간이 없거나 디지털 상품이면 비워두셔도 됩니다.",
    required: false,
  };
}

function getCompanyNamePlaceholder(domain: string): string {
  if (domain === "medical") return "예: 00의원";
  if (domain === "beauty") return "예: 00살롱";
  if (domain === "fnb") return "예: 00식당";
  if (domain === "experience") return "예: 00스튜디오";
  if (domain === "commerce") return "예: 00브랜드";
  return "예: 00컴퍼니";
}

function getStrengthsPlaceholder(profile: { domain: string; expertNoun: string; productNoun: string }): string {
  if (profile.domain === "medical") return "예: 원장님 직접 상담, 시술 경험, 내국인 재방문율, 전문 장비, 방송/보도 이력";
  if (profile.domain === "beauty") return "예: 대표 스타일, 포트폴리오, 전문가 경력, 외국인 고객 후기, 촬영/행사 고객 경험";
  if (profile.domain === "fnb") return "예: 대표 메뉴, 외국어 메뉴판, 기념일 세팅, 단체 예약 경험, 현지 고객 후기";
  if (profile.domain === "transport") return "예: 차량 컨디션, 기사 경력, 공항/관광지 운행 경험, 대형 차량 보유, 카시트 가능";
  if (profile.domain === "telecom") return "예: 공항 수령, 당일 개통, 외국어 설치 안내, 반납 편의, 분실/파손 안내 체계";
  if (profile.domain === "commerce") return "예: 단독 구성, 사은품, 면세/세금환급 안내, 픽업/배송 편의, 인기 상품 재고";
  return `예: ${profile.expertNoun} 운영 경험, 외국인 고객 후기, ${profile.productNoun} 구성 강점, 위치 접근성`;
}

function getEnvironmentPlaceholder(profile: { domain: string; facilityNoun: string }): string {
  if (profile.domain === "medical") return "예: 외국어 통역 스태프, 언어별 주의사항, 음료/다과, 역 인근, 짐 보관";
  if (profile.domain === "beauty") return "예: 외국어 응대, 스타일 상담, 짐 보관, 파우더룸, 역 인근, 포트폴리오 확인 가능";
  if (profile.domain === "fnb") return "예: 외국어 메뉴판, 아동 의자, 단체석, 주차, 예약 좌석 유지 시간, 알레르기 안내";
  if (profile.domain === "transport") return "예: 차량 종류, 탑승/수하물 가능 수량, 공항 미팅 장소, 대기 시간, 야간 운행 가능";
  if (profile.domain === "telecom") return "예: 수령/반납 장소, 설치 지원, 준비 서류, 영업시간, 문의 가능 채널";
  if (profile.domain === "commerce") return "예: 픽업 가능 시간, 배송 가능 지역, 교환/환불 창구, 재고 확인 방식, 포장 옵션";
  return `예: ${profile.facilityNoun} 위치, 이용 가능 시간, 외국어 응대, 준비물, 현장 추가금 안내`;
}

function getSlotQuantityLabel(domain: string): string {
  if (["tour", "ticket", "experience", "transport"].includes(domain)) return "회차/시간대별 최대 인원";
  if (domain === "fnb") return "시간대별 최대 예약 인원";
  if (["telecom", "commerce"].includes(domain)) return "동시 처리/판매 가능 수량";
  return "한 타임 예약 가능 최대 인원";
}

function getPriceModeLabel(domain: string): string {
  if (domain === "fnb") return "예약금 / 정산가";
  if (domain === "commerce") return "쿠폰 혜택 / 정산가";
  if (domain === "transport") return "차량 1대/1인 요금 구분";
  if (domain === "ticket") return "바우처 정산가";
  return "보증금 / 정산가";
}

export function buildProfileAdjustedIntakeStepsForData(data: IntakeData, baseSteps: IntakeStep[], config: FormConfigVersion = DEFAULT_FORM_CONFIG): IntakeStep[] {
  const profile = getCategoryProfile(data.category?.categoryId);
  if (!profile) return baseSteps;

  return baseSteps.map((step) => {
    if (step.id === "company") {
      let fields = patchField(step.fields, "storeNameKo", { label: `${profile.companyNameBaseLabel}(한국어)`, placeholder: getCompanyNamePlaceholder(profile.domain) });
      fields = patchField(fields, "storeNameEn", { label: `${profile.companyNameBaseLabel}(영어)`, placeholder: "영문 표기가 있다면 입력해주세요" });
      fields = patchField(fields, "storeNameJa", { label: `${profile.companyNameBaseLabel}(일본어)`, placeholder: "일본어 표기가 있다면 입력해주세요" });
      fields = patchField(fields, "storeNameZh", { label: `${profile.companyNameBaseLabel}(중국어)`, placeholder: "중국어 표기가 있다면 입력해주세요" });
      return {
        ...step,
        title: `${profile.facilityNoun} 기본 정보를 입력해주세요`,
        description: `상품 페이지와 예약 운영에 필요한 ${profile.facilityNoun} 정보입니다.`,
        fields,
      };
    }

    if (step.id === "reservation") {
      let fields = patchField(step.fields, "confirmationMode", {
        helperText: `기본값은 ‘승인대기 후 확정’입니다. 예약 접수 → ${profile.facilityNoun} 확인 → 예약확정 순서로 진행됩니다.`,
        options: [
          { label: "네, 확인 후 확정", value: "approval_required", description: `${profile.productNoun} 가능 여부를 확인하고 확정합니다. 대부분의 예약형 상품에 추천합니다.` },
          { label: "아니요, 바로 자동확정", value: "auto_confirm", description: "예약 시간만 비어 있으면 별도 확인 없이 바로 확정합니다." },
        ],
      });
      fields = patchField(fields, "communicationChannel", {
        options: [
          { label: "메일", value: "email", description: "예약 내역을 이메일로 받고 회신합니다." },
          { label: "카카오톡 단톡방", value: "kakao", description: "크리에이트립 담당자와 파트너 담당자가 함께 실시간으로 확인합니다." },
          { label: "크리에이트립 Admin", value: "creatrip_admin", description: "예약 내역을 직접 확인하고 관리할 수 있습니다." },
          { label: "소통 채널 불필요", value: "none", description: "자동확정 또는 쿠폰형 상품으로 운영할 때 선택합니다." },
        ],
      });
      return {
        ...step,
        title: ["commerce", "telecom"].includes(profile.domain) ? "운영 정보를 입력해주세요" : "예약 운영 정보를 입력해주세요",
        description: `${profile.facilityNoun} 이용 가능 시간과 예약 확정 방식을 입력해주세요.`,
        fields,
      };
    }

    if (step.id === "contract") {
      const isMedical = profile.domain === "medical";
      const fields = patchField(step.fields, "contractType", {
        helperText: isMedical
          ? "메디컬은 무료예약 + 업셀링 또는 보증금 결제 + 업셀링 중 선택합니다."
          : "일반 스팟은 업셀링 구조 없이 보증금 결제 또는 판매가/정산가 정산 방식으로 운영합니다.",
        options: [...MEDICAL_CONTRACT_OPTIONS, ...STANDARD_CONTRACT_OPTIONS],
      });
      return {
        ...step,
        description: isMedical
          ? "계약 구조는 아래 두 가지 중 하나를 선택합니다. 초반 예약 유도 효과를 우선하면 ‘무료예약 + 업셀링’, 노쇼 방지가 더 중요하면 ‘보증금 결제 + 업셀링’을 권장합니다."
          : "일반 스팟은 업셀링 수수료 구조를 사용하지 않습니다. 보증금 결제 방식 또는 판매가 판매 후 정산가 정산 방식 중 하나를 선택해주세요.",
        fields,
        tips: isMedical
          ? step.tips
          : [
            { title: "보증금 결제 방식", body: "• 예약 시 보증금을 결제해 노쇼를 줄입니다.\n• 방문 후 현장 결제 시 보증금을 차감하는 방식으로 운영합니다.\n• 업셀링 수수료 구조가 아닙니다.", kind: "info" },
            { title: "판매가 판매 후 정산가 정산 방식", body: "• 고객에게 판매가로 결제를 받고 이용/판매 완료 후 정산가 기준으로 정산합니다.\n• 티켓·쿠폰·상품처럼 판매가와 정산가를 미리 정하는 상품에 적합합니다.", kind: "success" },
          ],
      };
    }

    if (step.id === "products") {
      const consultationProductName = resolveConsultationProductName(profile.id, config);
      const offersConsultationProduct = isUpsellContractType(data.contract.contractType) && consultationProductName !== "";
      const nextStep = patchRepeaterFields(step, "products", (fields) => {
        let nextFields = patchRepeaterField(fields, "optionName", { label: `${profile.productNoun} 옵션명`, placeholder: profile.optionNamePlaceholder });
        if (!offersConsultationProduct) {
          nextFields = patchRepeaterField(nextFields, "optionName", { helperText: "" });
        }
        nextFields = patchRepeaterField(nextFields, "depositOrSettlementPrice", { label: getPriceModeLabel(profile.domain) });
        nextFields = patchRepeaterField(nextFields, "maxQuantityPerSlot", { label: getSlotQuantityLabel(profile.domain) });
        nextFields = patchRepeaterField(nextFields, "includedItems", { placeholder: profile.includedItemsPlaceholder });
        nextFields = patchRepeaterField(nextFields, "recommendationReason", { placeholder: profile.recommendationPlaceholder });
        nextFields = patchRepeaterField(nextFields, "detailDescription", { label: `${profile.productNoun} 상세 설명`, placeholder: profile.detailDescriptionPlaceholder });
        nextFields = patchRepeaterField(nextFields, "productPrecautions", { placeholder: profile.productPrecautionsPlaceholder });
        return nextFields;
      });
      return {
        ...nextStep,
        title: `${withObjectParticle(profile.productNoun)} 입력해주세요`,
        description: `판매할 핵심 ${profile.productNoun}의 구성과 가격을 입력해주세요.`,
        repeaters: nextStep.repeaters?.map((repeater) => (
          repeater.id === "products"
            ? { ...repeater, label: `${profile.productNoun} 옵션`, addLabel: `${profile.productNoun} 추가` }
            : repeater
        )),
        tips: offersConsultationProduct
          ? [
            { title: consultationProductName.includes("상담") ? "상담 상품 유지 추천" : "핵심 상품 추천", body: profile.consultationProduct.recommendationReason, kind: "success" },
            ...(step.tips?.slice(1) ?? []),
          ]
          : (step.tips?.slice(1) ?? []),
      };
    }

    if (step.id === "content") {
      let fields = patchField(step.fields, "catchphrase", { helperText: "대괄호 안을 실제 상품과 장점으로 바꿔주세요." });
      fields = patchField(fields, "sellingPointTags", { options: profile.sellingPointOptions });
      fields = patchField(fields, "targetCustomers", { options: profile.targetCustomerOptions });
      fields = patchField(fields, "clinicStrengths", { label: profile.strengthsLabel, placeholder: getStrengthsPlaceholder(profile) });
      fields = patchField(fields, "serviceEnvironment", { label: profile.serviceEnvironmentLabel, placeholder: getEnvironmentPlaceholder(profile) });
      if (profile.domain !== "medical") fields = fields?.filter((field) => field.id !== "cautionLanguages");
      return {
        ...step,
        title: "업체의 장점과 혜택을 입력해주세요",
        description: "외국인 고객이 예약할 이유를 실제 사례와 함께 알려주세요.",
        fields,
      };
    }

    if (step.id === "doctors") {
      return {
        ...step,
        title: `${profile.expertNoun} 이력을 입력해주세요`,
        description: `${profile.expertNoun} 소개 문구를 붙여넣거나 참고 링크를 남겨주세요.`,
        repeaters: step.repeaters?.map((repeater) => {
          if (repeater.id !== "doctors") return repeater;
          const fields = patchRepeaterField(repeater.fields as RepeaterFieldField[], "name", { placeholder: `예: ${profile.expertNoun} 이름 또는 팀명` });
          const nextFields = patchRepeaterField(fields, "role", { placeholder: `예: ${profile.expertNoun} / 운영 담당자` });
          const profileFields = patchRepeaterField(nextFields, "profileText", {
            placeholder: `${profile.expertNoun} 또는 운영팀 소개, 주요 경력, 외국인 고객 응대 경험, 참고할 만한 포트폴리오를 자유롭게 적어주세요.`,
            helperText: "기존 자료가 있다면 형식 그대로 복사해 붙여넣어도 됩니다. 사진이 있다면 첨부 영역에 올려주세요.",
          });
          return { ...repeater, fields: profileFields, label: profile.expertNoun, addLabel: `${profile.expertNoun} 추가`, description: `이름과 직함은 선택사항입니다. 이력 또는 참고 링크를 입력해주세요.` };
        }),
      };
    }

    if (step.id === "policy") {
      let fields = patchField(step.fields, "visitFlow", { label: profile.visitFlowLabel, defaultValue: profile.visitFlowDefault, helperText: `기본 ${profile.visitFlowLabel}를 넣어두었습니다. 실제 흐름과 다르면 수정해주세요.` });
      fields = patchField(fields, "bookingQuestionChecklist", { options: profile.bookingQuestionOptions, defaultValue: profile.bookingQuestionChecklistDefault, helperText: getBookingQuestionChecklistHelperText(profile.domainLabel) });
      fields = patchField(fields, "healthRestrictions", { label: profile.domain === "medical" || profile.domain === "beauty" ? "사전 확인이 필요한 건강 상태" : "사전 확인이 필요한 이용 조건", options: profile.healthRestrictionOptions, defaultValue: profile.healthRestrictionsDefault });
      fields = patchField(fields, "otherPrecautions", { placeholder: `${profile.otherPrecautionsPlaceholder}\n\n카테고리: ${profile.domainLabel} > ${profile.label}` });
      if (profile.domain !== "medical") fields = fields?.filter((field) => !MEDICAL_ONLY_POLICY_FIELD_IDS.has(field.id));
      return {
        ...step,
        title: "정책과 이용 안내를 확인해주세요",
        description: `${profile.productNoun} 이용 전후 고객에게 안내할 정책과 주의사항을 확인해주세요.`,
        fields,
      };
    }

    if (step.id === "photos") {
      return {
        ...step,
        description: `상품 페이지에 사용할 가로형 고해상도 사진을 첨부해주세요.`,
        fileUploads: step.fileUploads?.map((upload) => {
          if (upload.id === "interiorExterior") return { ...upload, ...getInteriorExteriorPhotoCopy(profile) };
          if (upload.id === "treatmentPhotos") return { ...upload, label: `${profile.productNoun} 진행/이용 장면 또는 관련 자료`, description: `${withSubjectParticle(profile.productNoun)} 실제로 진행되거나 사용되는 장면, 메뉴·코스·차량·소품·도구 등 준비된 자료를 첨부해주세요.` };
          if (upload.id === "beforeAfterPhotos") {
            const label = profile.domain === "medical"
              ? "전후 비교 사진"
              : ["beauty", "experience"].includes(profile.domain)
                ? "전후 / 포트폴리오 사진"
                : "결과물 / 이용 예시 사진";
            const description = profile.domain === "medical"
              ? upload.description
              : ["beauty", "experience"].includes(profile.domain)
                ? "서비스 전후 결과, 포트폴리오, 스타일 예시 사진이 있다면 첨부해주세요."
                : "고객이 결과물이나 실제 이용 모습을 이해하는 데 도움이 되는 예시 사진이 있다면 첨부해주세요.";
            return { ...upload, label, description };
          }
          return upload;
        }),
      };
    }

    if (step.id === "documents") {
      const fileUploads = profile.documentMode === "medical"
        ? step.fileUploads
        : step.fileUploads?.map((upload) => (
          upload.id === "medicalTourismCertificate"
            ? { ...upload, required: false, label: "외국인유치의료기관 등록증 사본 (의료기관만 해당)", description: "의료기관이 아니라면 첨부하지 않아도 됩니다." }
            : upload
        ));
      return {
        ...step,
        description: profile.documentMode === "medical"
          ? "계약과 거래처 등록에 필요한 서류입니다. 의료기관은 외국인유치의료기관 등록증도 함께 첨부해주세요."
          : "계약과 거래처 등록에 필요한 서류입니다. 의료기관 관련 서류는 해당되는 경우에만 첨부해주세요.",
        fileUploads,
      };
    }

    return step;
  });
}
